import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { convertToCanonicalFormat } from '@/utils/canonicalStructure';
import { useUserName } from '@/hooks/useUserName';
import { useUserEmail } from '@/hooks/useUserEmail';

interface DeleteConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  solutionId: string;
  solutionName: string;
  solutionDetails: any;
  onDeleteSuccess: () => void;
}

const DeleteConfirmationDialog = ({
  isOpen,
  onClose,
  solutionId,
  solutionName,
  solutionDetails,
  onDeleteSuccess,
}: DeleteConfirmationDialogProps) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { userName: persistentUserName, setUserName: setPersistentUserName } = useUserName();
  const { userEmail: persistentEmail, setUserEmail: setPersistentEmail } = useUserEmail();
  
  const [userName, setUserName] = useState(persistentUserName || '');
  const [userEmail, setUserEmail] = useState(persistentEmail || '');

  useEffect(() => {
    if (persistentUserName) {
      setUserName(persistentUserName);
    }
    if (persistentEmail) {
      setUserEmail(persistentEmail);
    }
  }, [persistentUserName, persistentEmail]);

  const handleDelete = async () => {
    if (!userName.trim() || !userEmail.trim()) {
      toast({
        title: "Missing Information",
        description: "Please provide both your name and email address.",
        variant: "destructive",
      });
      return;
    }

    if (!confirmationChecked) {
      toast({
        title: "Confirmation Required",
        description: "Please confirm that you understand this is a deletion request.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    
    try {
      // Submit delete request as edit suggestion with "to_classify" status
      const canonicalDetails = convertToCanonicalFormat(solutionDetails);
      
      const { error } = await supabase
        .from('edit_suggestions')
        .insert({
          solution_id: solutionId,
          suggester_name: userName.trim(),
          email: userEmail.trim(),
          status: 'to_classify',
          suggestion: JSON.stringify(canonicalDetails)
        });
      
      if (error) throw error;

      // Update persistent storage
      setPersistentUserName(userName.trim());
      setPersistentEmail(userEmail.trim());
      
      toast({
        title: "Deletion request submitted",
        description: `Your deletion request for "${solutionName}" has been submitted and is pending admin review.`,
        variant: "default",
      });
      
      onDeleteSuccess();
    } catch (error) {
      console.error('Error submitting delete request:', error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your deletion request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConfirmationChecked(false);
      setUserName('');
      setUserEmail('');
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationChecked(false);
      setUserName(persistentUserName || '');
      setUserEmail(persistentEmail || '');
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertDialogTitle className="text-red-900">
              Request Solution Deletion
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to request deletion of <strong>"{solutionName}"</strong>?
              </p>
              <p className="text-sm text-muted-foreground">
                This will submit a deletion request that requires admin approval. The solution will remain visible until an admin reviews and approves your request.
              </p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="delete-user-name" className="text-sm font-medium">
                    Your Name
                  </Label>
                  <Input
                    id="delete-user-name"
                    type="text"
                    placeholder="John Doe"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    disabled={isDeleting}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-user-email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="delete-user-email"
                    type="email"
                    placeholder="john@example.com"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    disabled={isDeleting}
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2 p-3 bg-red-50 rounded-md border border-red-200">
                <Checkbox
                  id="confirm-delete"
                  checked={confirmationChecked}
                  onCheckedChange={(checked) => setConfirmationChecked(checked as boolean)}
                  disabled={isDeleting}
                />
                <label 
                  htmlFor="confirm-delete" 
                  className="text-sm font-medium text-red-800 cursor-pointer"
                >
                  I understand this submits a deletion request for admin review
                </label>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={!confirmationChecked || isDeleting}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {isDeleting ? "Submitting..." : "Submit Deletion Request"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteConfirmationDialog; 